import AreaChartCard from "components/Charts/AreaChartCard";
import LineChartCard from "components/Charts/LineChartCard";
import AlertBanner from "components/AlertBanner";
import { useAuth } from "context/AuthContext";
import DeviceSelector from "components/DeviceSelector";
import DeviceConfigPanel from "components/device/DeviceConfigPanel";
import StatusCard from "components/StatusCard";
import useDistanceCalibration from "hooks/useDistanceCalibration";
import { useDevices } from "hooks/useDevices";
import {
  Battery,
  Calendar,
  Clock,
  Database,
  Droplets,
  Edit2,
  Eye,
  MapPin,
  RefreshCw,
  Settings,
  Signal,
  Thermometer,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getDeviceData, getProcessedWaterLevel } from "services/api";
import { classifyDeviceStatus } from "utils/deviceStatus";

function formatDateForInput(date) {
  const year  = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day   = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const mins  = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${mins}`;
}

export default function DashboardPage() {
  const auth = useAuth();
  const isStaff = Boolean(auth?.user?.is_staff);
  const { macAddress } = useParams();
  const navigate = useNavigate();
  // Overview (readings and charts) or Configuration (the device's settings); ?tab=config so it can be linked.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "config" ? "config" : "overview";
  const setTab = (next) => setSearchParams(next === "config" ? { tab: "config" } : {}, { replace: true });
  const { devices, selectedDevice, setSelectedDevice, refetch: refetchDevices } = useDevices();

  const [readings, setReadings] = useState([]);
  const [deviceConfig, setDeviceConfig] = useState(null);
  const [processedData, setProcessedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const [startTime, setStartTime] = useState(() => {
    const d = new Date();
    return formatDateForInput(new Date(d.getTime() - 3 * 24 * 60 * 60 * 1000));
  });
  const [endTime, setEndTime] = useState(() => formatDateForInput(new Date()));
  const [useDateFilter, setUseDateFilter] = useState(true);

  const refreshInterval = 30000;

  const { enhancedData, latestData, statistics } = useDistanceCalibration(readings, deviceConfig);

  // The level from our own range estimate (water_level_est_cm, offset snapshot - d1_est; see CLAUDE.md
  // "Estimated water level"): only a node on firmware .62 or later reports one, so the line is drawn only then.
  const hasLevelEstimate = useMemo(
    () => enhancedData.some((row) => typeof row.water_level_est_cm === "number"),
    [enhancedData]
  );

  useEffect(() => {
    if (!macAddress) return;
    const decodedMac = decodeURIComponent(macAddress);
    if (decodedMac !== selectedDevice) setSelectedDevice(decodedMac);
  }, [macAddress, selectedDevice, setSelectedDevice]);

  const calculateWaterLevel = (distanceCm, offsetCm) => {
    if (!distanceCm || distanceCm <= 0 || !offsetCm) return 0;
    return Math.round((offsetCm - distanceCm) * 10) / 10; // negative = below ground
  };

  const getTemperatureStatus = (temp) => {
    if (temp < 0)  return { status: "Freezing", color: "blue" };
    if (temp < 10) return { status: "Cold",     color: "cyan" };
    if (temp < 25) return { status: "Cool",     color: "green" };
    if (temp < 35) return { status: "Warm",     color: "orange" };
    return         { status: "Hot",             color: "red" };
  };

  const getHumidityStatus = (humidity) => {
    if (humidity < 30) return { status: "Dry",         color: "orange" };
    if (humidity < 60) return { status: "Comfortable", color: "green" };
    return             { status: "Humid",              color: "blue" };
  };

  // Cellular signal quality from RSSI in dBm (A7672S +CSQ range -113 to -50)
  const getRssiStatus = (rssi) => {
    if (rssi == null)  return { status: "No signal", color: "gray" };
    if (rssi >= -70)   return { status: "Excellent", color: "green" };
    if (rssi >= -85)   return { status: "Good",      color: "green" };
    if (rssi >= -100)  return { status: "Fair",      color: "orange" };
    return             { status: "Poor",             color: "red" };
  };

  const setDefaultDateRange = () => {
    const now = new Date();
    setStartTime(formatDateForInput(new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000)));
    setEndTime(formatDateForInput(now));
  };

  const setQuickRange = (hours) => {
    const now = new Date();
    setStartTime(formatDateForInput(new Date(now.getTime() - hours * 60 * 60 * 1000)));
    setEndTime(formatDateForInput(now));
  };

  const fetchReadings = async (showLoading = true) => {
    if (!selectedDevice) return;
    if (showLoading) setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ limit: "1000" });
      if (useDateFilter) {
        if (startTime) params.append("start_time", new Date(startTime).toISOString());
        if (endTime)   params.append("end_time",   new Date(endTime).toISOString());
      }

      const data = await getDeviceData(selectedDevice, params.toString());
      setReadings(data.readings);
      setDeviceConfig(data.device_config);

      setLastUpdate(new Date());
      return data.readings;
    } catch (err) {
      console.error("Error fetching readings:", err);
      setError(err.message);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const fetchProcessed = async (readings) => {
    if (!selectedDevice) return;
    try {
      // Align the Kalman window to exactly the readings that were returned,
      // so both charts always cover the same time period regardless of the
      // 1000-reading limit applied by the raw endpoint.
      let start = null;
      let end = null;
      if (readings && readings.length > 0) {
        const timestamps = readings.map((r) => r.timestamp).sort();
        start = timestamps[0];
        end = timestamps[timestamps.length - 1];
      } else if (useDateFilter) {
        start = startTime || null;
        end = endTime || null;
      }
      const result = await getProcessedWaterLevel(selectedDevice, start, end);
      setProcessedData(
        (result.data || []).map((row) => ({
          ...row,
          timeNumeric: new Date(row.timestamp).getTime(),
        }))
      );
    } catch (err) {
      console.error("Error fetching processed water level:", err);
    }
  };

  const fetchAll = async (showLoading = true) => {
    const fetchedReadings = await fetchReadings(showLoading);
    await fetchProcessed(fetchedReadings);
  };

  useEffect(() => {
    if (selectedDevice) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice]);

  useEffect(() => {
    if (useDateFilter && selectedDevice) fetchAll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useDateFilter, startTime, endTime]);

  useEffect(() => {
    if (!autoRefresh || !selectedDevice || useDateFilter) return;
    const interval = setInterval(() => fetchAll(false), refreshInterval);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, selectedDevice, useDateFilter]);

  const handleRefresh = () => {
    refetchDevices();
    if (selectedDevice) fetchAll();
  };

  const handleDateFilterToggle = (enabled) => {
    setUseDateFilter(enabled);
    if (enabled && (!startTime || !endTime)) setDefaultDateRange();
  };

  const getDeviceStatus = (device) => {
    const status = classifyDeviceStatus(device.last_seen);
    // Kept as its own (color, label) shape - callers here predate the
    // shared bg-*/text-* Tailwind classes classifyDeviceStatus returns.
    const color =
      status.status === "never" ? "text-gray-400"
      : status.status === "online" ? "text-green-500"
      : status.status === "recent" ? "text-yellow-500"
      : "text-red-500";
    return { color, label: status.label };
  };

  if (loading && readings.length === 0) {
    return (
      <div className="container mx-auto px-3 sm:px-4 py-6">
        <div className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10 p-6">
          <div className="flex items-center justify-center py-12">
            <Database className="w-12 h-12 text-blue-500 mr-4 animate-spin" />
            <div className="text-center">
              <p className="text-lg font-medium text-on-surface">Loading Dashboard...</p>
              <p className="text-sm text-on-surface-variant">Fetching device data</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Device</p>
          <h1 className="text-3xl font-extrabold font-headline text-on-surface tracking-tight truncate">
            {devices.find((d) => d.mac_address === selectedDevice)?.name || selectedDevice || "—"}
          </h1>
          {selectedDevice && (
            <p className="text-xs font-mono text-on-surface-variant mt-1 truncate">{selectedDevice}</p>
          )}
        </div>
        <div className="shrink-0 min-w-[200px]">
          <p className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest mb-1">Device</p>
          <DeviceSelector
            devices={devices}
            value={selectedDevice}
            onChange={(mac) =>
              navigate(`/devices/${encodeURIComponent(mac)}${tab === "config" ? "?tab=config" : ""}`)
            }
            showReadings
          />
          {lastUpdate && (
            <p className="text-xs font-medium text-on-surface-variant mt-1 whitespace-nowrap">
              Updated {lastUpdate.toLocaleTimeString()}
            </p>
          )}
        </div>
      </div>

      <AlertBanner type="error"   message={error}   onDismiss={() => setError(null)} />

      <div className="flex border-b border-outline-variant/30 overflow-x-auto" role="tablist">
        {[
          { key: "overview", label: "Overview", Icon: Eye },
          { key: "config", label: "Configuration", Icon: Settings },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 flex-shrink-0 px-4 sm:px-6 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === key
                ? "border-primary text-primary"
                : "border-transparent text-on-surface-variant hover:text-on-surface"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === "config" && selectedDevice && (
        <DeviceConfigPanel
          deviceMac={selectedDevice}
          isStaff={isStaff}
          onSaved={async () => {
            refetchDevices();
            await fetchAll(false);
          }}
        />
      )}

      {tab === "overview" && (
        <>
          {/* Control Panel */}
          <div className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10 p-4">
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={autoRefresh && !useDateFilter}
                      disabled={useDateFilter}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    Auto-refresh ({refreshInterval / 1000}s)
                  </label>
                </div>

                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-on-primary rounded-full hover:bg-primary-container disabled:opacity-50 text-sm transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                  Refresh
                </button>
              </div>

              {/* Date Filter */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-4 mb-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={useDateFilter}
                      onChange={(e) => handleDateFilterToggle(e.target.checked)}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <Calendar className="w-4 h-4" />
                    Filter by date range
                  </label>

                  {useDateFilter && (
                    <div className="flex gap-2 flex-wrap">
                      {[{ label: "Last Hour", h: 1 }, { label: "Last 24h", h: 24 }, { label: "Last Week", h: 168 }].map(
                        ({ label, h }) => (
                          <button
                            key={h}
                            onClick={() => setQuickRange(h)}
                            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                          >
                            {label}
                          </button>
                        )
                      )}
                    </div>
                  )}
                </div>

                {useDateFilter && (
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Start:</label>
                      <input
                        type="datetime-local"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700 whitespace-nowrap">End:</label>
                      <input
                        type="datetime-local"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Dashboard Content */}
          {latestData && deviceConfig ? (
            <div className="space-y-8">
              {/* Environmental Sensors */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <StatusCard
                  title="Temperature"
                  value={latestData.ext_temperature ?? latestData.temperature}
                  unit="°C"
                  icon={Thermometer}
                  color={getTemperatureStatus(latestData.ext_temperature ?? latestData.temperature).color}
                  status={
                    latestData.ext_temperature != null
                      ? `External · ${getTemperatureStatus(latestData.ext_temperature).status}`
                      : `Internal · ${getTemperatureStatus(latestData.temperature).status}`
                  }
                  subtitle={
                    latestData.ext_temperature != null
                      ? `Internal: ${latestData.temperature.toFixed(1)} °C`
                      : undefined
                  }
                />
                <StatusCard
                  title="Humidity"
                  value={latestData.ext_humidity ?? latestData.humidity}
                  unit="%"
                  icon={Droplets}
                  color={getHumidityStatus(latestData.ext_humidity ?? latestData.humidity).color}
                  status={
                    latestData.ext_humidity != null
                      ? `External · ${getHumidityStatus(latestData.ext_humidity).status}`
                      : `Internal · ${getHumidityStatus(latestData.humidity).status}`
                  }
                  subtitle={
                    latestData.ext_humidity != null
                      ? `Internal: ${latestData.humidity.toFixed(1)} %`
                      : undefined
                  }
                />
                {(() => {
                  const soc = latestData.battery_soc;
                  const v = latestData.battery_voltage;
                  const color = soc == null ? "green" : soc > 50 ? "green" : soc > 20 ? "yellow" : "red";
                  return (
                    <StatusCard
                      title="Battery"
                      value={soc != null ? Math.round(soc) : v}
                      unit={soc != null ? "%" : "V"}
                      subtitle={soc != null && v != null ? `${v.toFixed(2)} V` : undefined}
                      icon={Battery}
                      color={color}
                      decimals={0}
                    />
                  );
                })()}
              </div>

              {/* Power Monitoring + Latest Reading */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <StatusCard title="Solar Voltage" value={latestData.solar_voltage} unit="V" icon={Zap} color="yellow" />
                <StatusCard
                  title="Cellular Signal"
                  value={latestData.rssi}
                  unit="dBm"
                  icon={Signal}
                  color={getRssiStatus(latestData.rssi).color}
                  status={getRssiStatus(latestData.rssi).status}
                  decimals={0}
                />
                <StatusCard
                  title="Latest Reading"
                  value={new Date(latestData.timestamp).toLocaleString()}
                  icon={Clock}
                  color="blue"
                />
              </div>

              {/* Device Location */}
              {deviceConfig.last_latitude != null && deviceConfig.last_longitude != null && (
                <div className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10 overflow-hidden">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant/10">
                    <h2 className="text-lg font-bold text-on-surface flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-rose-500" />
                      Device Location
                    </h2>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${deviceConfig.last_latitude},${deviceConfig.last_longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      Open in Maps ↗
                    </a>
                  </div>
                  <div className="relative" style={{ height: 220 }}>
                    <iframe
                      title="Device location"
                      width="100%"
                      height="100%"
                      style={{ border: 0, display: "block" }}
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${deviceConfig.last_longitude - 0.01},${deviceConfig.last_latitude - 0.01},${deviceConfig.last_longitude + 0.01},${deviceConfig.last_latitude + 0.01}&layer=mapnik&marker=${deviceConfig.last_latitude},${deviceConfig.last_longitude}`}
                    />
                  </div>
                  <div className="px-6 py-3 bg-slate-50 border-t border-outline-variant/10 text-xs font-mono text-slate-500">
                    {deviceConfig.last_latitude.toFixed(6)}, {deviceConfig.last_longitude.toFixed(6)}
                    {deviceConfig.last_gps_at && (
                      <span className="ml-3 font-sans text-slate-400">
                        · GPS at {new Date(deviceConfig.last_gps_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Water Level Monitoring */}
              {(latestData.distance_1 || latestData.distance_1_calibrated) && (
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl shadow-sm border p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-on-surface flex items-center gap-2">
                      <Droplets className="w-6 h-6 text-blue-600" />
                      Water Level Monitoring
                    </h3>
                    <button
                      onClick={() => setTab("config")}
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit water level setup
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <StatusCard
                      title="Raw Water Level - Sensor 1"
                      subtitle={`Distance: ${latestData.distance_1?.toFixed(1) || "N/A"} cm | Offset: ${deviceConfig.distance_1_offset} cm`}
                      value={latestData.waterLevel1}
                      unit="cm"
                      icon={Droplets}
                      color="blue"
                    />
                    <StatusCard
                      title="Calibrated Water Level - Sensor 1"
                      subtitle={`Calibrated: ${latestData.distance_1_calibrated?.toFixed(1) || "N/A"} cm | Offset: ${deviceConfig.distance_1_offset} cm`}
                      value={latestData.waterLevel1Calibrated}
                      unit="cm"
                      icon={Eye}
                      color="cyan"
                    />

                  </div>

                </div>
              )}

              {/* Historical Charts */}
              {enhancedData.length > 1 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Kalman-processed water level — top, full width */}
                  {processedData.length > 1 && (
                    <AreaChartCard
                      title="Processed Water Level (Kalman · Hourly)"
                      historicalData={processedData}
                      className="lg:col-span-2"
                      yDomain={[(min) => Math.min(min, -30), (max) => Math.max(max, 50)]}
                      zeroLine
                      areas={[
                        {
                          dataKey: "water_level_cm",
                          name: "Kalman (cm)",
                          stroke: "#6366f1",
                          fill: "#6366f1",
                          fillOpacity: 0.2,
                          strokeWidth: 2,
                          baseValue: "dataMin",
                          dot: (props) => {
                            if (!props.payload?.temp_gated) return <g key={props.key} />;
                            return (
                              <circle
                                key={props.key}
                                cx={props.cx}
                                cy={props.cy}
                                r={5}
                                fill="#ef4444"
                                stroke="white"
                                strokeWidth={1.5}
                              />
                            );
                          },
                        },
                      ]}
                    />
                  )}

                  {(latestData.waterLevel1 !== undefined || latestData.waterLevel1Calibrated !== undefined) && (
                    <LineChartCard
                      title="Water Level Monitoring - Sensor 1"
                      historicalData={enhancedData}
                      className="lg:col-span-2"
                      syncId="sensorDashboard"
                      yDomain={[(min) => Math.min(min, -30), (max) => Math.max(max, 50)]}
                      zeroLine
                      lines={[
                        { dataKey: "waterLevel1",           name: "Raw (cm)",                   color: "#94a3b8", strokeWidth: 1, strokeDasharray: "5 5" },
                        { dataKey: "waterLevel1Calibrated",  name: "Physics Calibrated (cm)",    color: "#3b82f6", strokeWidth: 2 },
                        { dataKey: "waterLevel1Empirical",   name: "Empirical Calibrated (cm)",  color: "#10b981", strokeWidth: 2 },
                        ...(hasLevelEstimate
                          ? [{ dataKey: "water_level_est_cm", name: "Estimated (cm)", color: "#f59e0b", strokeWidth: 2 }]
                          : []),
                      ]}
                    />
                  )}

                  {latestData.distance_1 !== undefined && (
                    <LineChartCard
                      title="Distance Sensor 1"
                      historicalData={enhancedData}
                      syncId="sensorDashboard"
                      lines={[
                        { dataKey: "distance_1",            name: "Raw (cm)",       color: "#94a3b8", strokeDasharray: "5 5" },
                        { dataKey: "distance_1_calibrated",  name: "Physics (cm)",   color: "#3b82f6" },
                        { dataKey: "distance_1_empirical",   name: "Empirical (cm)", color: "#10b981" },
                      ]}
                    />
                  )}

                  <LineChartCard
                    title="Temperature"
                    historicalData={enhancedData}
                    syncId="sensorDashboard"
                    lines={[
                      { dataKey: "ext_temperature", name: "External (°C)", color: "#ef4444", strokeWidth: 2 },
                      { dataKey: "temperature",     name: "Internal (°C)", color: "#fca5a5", strokeWidth: 1, strokeDasharray: "4 3" },
                    ]}
                  />
                  <LineChartCard
                    title="Humidity"
                    historicalData={enhancedData}
                    syncId="sensorDashboard"
                    lines={[
                      { dataKey: "ext_humidity", name: "External (%)", color: "#3b82f6", strokeWidth: 2 },
                      { dataKey: "humidity",     name: "Internal (%)", color: "#93c5fd", strokeWidth: 1, strokeDasharray: "4 3" },
                    ]}
                  />
                  <LineChartCard
                    title="Battery & Solar Voltage"
                    historicalData={enhancedData}
                    className="lg:col-span-2"
                    syncId="sensorDashboard"
                    lines={[
                      { dataKey: "battery_voltage", name: "Battery (V)",  color: "#10b981", yAxisId: "voltage" },
                      { dataKey: "solar_voltage",   name: "Solar (V)",    color: "#eab308", yAxisId: "voltage" },
                      { dataKey: "battery_soc",     name: "Battery (%)",  color: "#6366f1", yAxisId: "soc" },
                    ]}
                  />
                </div>
              )}

              {statistics && (
                <div className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10 p-6">
                  <h3 className="text-lg font-semibold mb-4">Statistical Summary</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                    {[
                      { label: "Data Points",    value: statistics.count, fmt: (v) => v },
                      { label: "Mean Distance",  value: statistics.mean,  fmt: (v) => `${v.toFixed(2)} cm` },
                      { label: "Std Dev",        value: statistics.stdDev, fmt: (v) => `${v.toFixed(2)} cm` },
                      { label: "Min",            value: statistics.min,   fmt: (v) => `${v.toFixed(2)} cm` },
                      { label: "Max",            value: statistics.max,   fmt: (v) => `${v.toFixed(2)} cm` },
                    ].map(({ label, value, fmt }) => (
                      <div key={label}>
                        <p className="text-sm text-on-surface-variant">{label}</p>
                        <p className="text-xl font-bold">{fmt(value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10 p-6">
              <div className="text-center py-12">
                <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium">No sensor data available</p>
                <p className="text-gray-400 text-sm mt-2">
                  {selectedDevice ? "No data found for selected device" : "Please select a device to view data"}
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
